import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes, css } from 'styled-components';
import { vendorLogosList } from '../../../helper/constant';
import { breakpoints } from '../../../styles/screenSizes';

const Wrapper = styled.section`
    padding-top: 78px;
    z-index: 4;
    overflow: hidden;

    @media only screen and (max-width: ${breakpoints.md}) {
        padding-top: 64px;
    }
    @media only screen and (max-width: ${breakpoints.sm}) {
        padding-top: 36px;
    }
`;

const Content = styled.div`
    overflow: hidden;
    max-width: 1280px;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 36px;
    padding: 0 26px;

    @media only screen and (max-width: ${breakpoints.md}) {
        padding: 0 16px;
        gap: 24px;
    }

    @media only screen and (max-width: ${breakpoints.sm}) {
        padding: 0px;
    }
`;

const HeadingContainer = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 4px;
`;

const HeadingText = styled.h2`
    color: ${({ theme }) => (theme === 'light' ? '#262626' : '#fff')};
    font-size: 20px;
    font-weight: 500;
    justify-content: center;

    @media only screen and (max-width: ${breakpoints.md}) {
        font-size: 14px;
    }
`;

const SharedStyle = css`
    content: '';
    position: absolute;
    height: 100%;
    width: 230px;
    top: 0;
    bottom: 0;
    z-index: 1;

    @media screen and (max-width: ${breakpoints.sm}) {
        width: 150px;
    }
`;

const MarqueeWrapper = styled.div`
    position: relative;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;

    ${({ theme, effectColor }) =>
        theme === 'light' &&
        css`
            &::before {
                ${SharedStyle}
                background: linear-gradient(to right, ${effectColor} 10%, transparent 90%);
                left: 0;
            }

            &::after {
                ${SharedStyle}
                background: linear-gradient(90deg, transparent 10%, ${effectColor} 90%);
                right: 0;
            }
        `}
`;

const ScrollX = keyframes`
    from{
        left: translateX(0);
    }
    to {
        transform: translateX(-100%);
    }
`;

const MarqueeGroup = styled.div`
    display: inline-flex;
    animation: ${ScrollX} 20s linear infinite;
`;

const LogoContainer = styled.div`
    height: 40px;
    width: max-content;
    display: flex;
    align-items: end;
    justify-content: center;
    margin: 0 30px;

    ${({ name }) => {
        if (name === 'TransUnion') {
            return css`
                margin-top: 3px;

                @media only screen and (max-width: ${breakpoints.md}) {
                    margin-top: 0px;
                }
            `;
        }

        if (name === 'Thomson Reuters') {
            return css`
                margin-bottom: 3px;

                @media only screen and (max-width: ${breakpoints.md}) {
                    margin-bottom: 0px;
                }
            `;
        }
    }}

    @media only screen and (max-width: ${breakpoints.md}) {
        margin: 0 20px;
        height: 20px;
    }

    @media only screen and (max-width: ${breakpoints.sm}) {
        margin: 0 14px;
        height: 14px;
    }
`;

const CompanyLogo = styled.img`
    height: ${({ customSize }) => (customSize ? '16px' : 'auto')};
    width: auto;
    filter: ${({ theme }) => theme !== 'light' && 'invert(1)'};

    @media only screen and (max-width: ${breakpoints.md}) {
        height: ${({ customSize }) => (customSize ? '16px' : '100%')};
        object-fit: contain;
    }

    @media only screen and (max-width: ${breakpoints.md}) {
        height: ${({ customSize }) => (customSize ? '12px' : '100%')};
    }
`;

const Vendors = ({ theme = 'light', effectColor = '#fff' }) => {
    return (
        <Wrapper>
            <Content>
                <HeadingContainer>
                    <HeadingText theme={theme}>Authenticate is trusted by</HeadingText>
                </HeadingContainer>

                <MarqueeWrapper theme={theme} effectColor={effectColor}>
                    {Array.from({ length: 2 }).map((_, index) => (
                        <MarqueeGroup key={index + 1}>
                            {vendorLogosList.map((data) => (
                                <LogoContainer key={data.id} name={data.name}>
                                    <CompanyLogo
                                        src={data.logo}
                                        alt={data.name}
                                        customSize={data?.customSize}
                                        theme={theme}
                                    />
                                </LogoContainer>
                            ))}
                        </MarqueeGroup>
                    ))}
                </MarqueeWrapper>
            </Content>
        </Wrapper>
    );
};

Vendors.propTypes = {
    effectColor: PropTypes.string,
    theme: PropTypes.string,
};

export default Vendors;
